import classnames from 'classnames'
import { useRouter } from 'next-nprogress-bar'
import { CSSProperties, forwardRef, useMemo, useRef } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant, navigateToPDP } from '@src/components/product-card/product-card.helpers'
import { Skeleton } from '@src/quince-components/src'
import { RichText } from '@src/quince-components/src/molecules'
import POSITIONS from '@src/quince-site-common/src/constants/positions'
import { QUINCE_BOT_LOCATORS } from '@src/quince-site-common/src/constants/quinceBot'
import { useLocalisationFlowsExperimentContext } from '@src/quince-site-common/src/contexts/localisation-flow-experiement'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getDisplayKey } from '@src/utils/i18n.utils'

import { trackingMetaData } from '../../collection.constants'
import { SubCollection } from '../../domains/Collection'

import styles from './subCollectionSection.module.scss'

const TRACKING_CATEGORY_NAVIGATION = 'navigation'

interface SubCollectionSectionProps {
  className?: string
  subCollectionData: SubCollection
  isFirstCollection?: boolean
  showBorderBottom?: boolean
  tracker: string
  shouldShowAllVariants?: boolean
  additionalAttributes?: Record<string, string>
  collectionGender: GenderName | undefined
  doHolidayCutoffSorting: boolean
  collectionSlug: string
  productViewTracker: string
  showSecondImageOnProductTiles?: boolean
  showShimmerOnProductCards?: boolean
  isNewCLPFiltersExperimentActive: boolean
  showDesktopFilterWithSidebar: boolean
  showMixedPage: boolean
  isBundleCLP: boolean
}

type makeGridPositionStyle = (options: {
  horizontalPosition: POSITIONS
  noOfRows: number
  sizeInNumberOfRows: number
  maxColumnPosition: number
}) => CSSProperties

const LEFT_POSITIONS = new Set([POSITIONS.BOTTOM_LEFT, POSITIONS.LEFT, POSITIONS.TOP_LEFT])
const RIGHT_POSITIONS = new Set([POSITIONS.BOTTOM_RIGHT, POSITIONS.RIGHT, POSITIONS.TOP_RIGHT])

const makeGridPositionStyle: makeGridPositionStyle = ({
  horizontalPosition,
  noOfRows,
  // sizeInNumberOfRows,
  maxColumnPosition,
}) => {
  return {
    gridRowStart: noOfRows === 1 ? 1 : noOfRows - 1,
    gridRowEnd: `span 2`,
    gridColumnStart: LEFT_POSITIONS.has(horizontalPosition) ? 1 : maxColumnPosition,
    gridColumnEnd: RIGHT_POSITIONS.has(horizontalPosition) ? maxColumnPosition : 1,
  }
}

const SubCollectionSection = forwardRef<HTMLDivElement, SubCollectionSectionProps>(
  (
    {
      subCollectionData,
      className,
      isFirstCollection = true,
      showBorderBottom,
      tracker,
      additionalAttributes,
      collectionGender,
      productViewTracker,
      showSecondImageOnProductTiles = false,
      showShimmerOnProductCards = false,
      isNewCLPFiltersExperimentActive,
      showDesktopFilterWithSidebar,
      showMixedPage,
      isBundleCLP,
    },
    _ref, // Renamed to _ref to match unused arg pattern
  ) => {
    const router = useRouter()
    const { deviceType } = useDeviceInfo()
    const { isNewLocalisedFlow } = useLocalisationFlowsExperimentContext()
    const isDesktop = deviceType === DEVICE_TYPES.DESKTOP
    const productCards = subCollectionData.productCards

    const imageRef = useRef<HTMLImageElement | null>(null)

    const maxWidth = getMaxWidthInPx({
      maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
      maxWidthDesktopInRem: styles.productCardWidth ?? '',
      deviceType: deviceType,
    })

    const subTitleProps = useMemo(
      () => RichTextDomain.makeRichTextProps(subCollectionData.subTitle),
      [subCollectionData.subTitle],
    )

    if (showShimmerOnProductCards) {
      return (
        <div
          className={classnames(styles.productCardsSkeletonWrapper, {
            [`${styles.productCardsSkeletonWrapper__new_clp_filters_experiment_group_b}`]: showDesktopFilterWithSidebar,
          })}
          data-testid="product-card-skeleton"
        >
          {new Array(isDesktop ? 8 : 4).fill(0).map((_, index) => (
            <Skeleton key={index} className={styles.productCardSkeleton} />
          ))}
        </div>
      )
    }

    return (
      <div
        data-testid="sub-collection-section-wrapper"
        data-content-type="sub-collection"
        className={classnames(styles.wrapper, className, {
          [styles.wrapper__hasPaddingBottom ?? '']: true,
          [styles.wrapper__hasBorderBottom ?? '']: isDesktop && showBorderBottom,
          [`${styles.wrapper__new_clp_filters_experiment}`]:
            isNewCLPFiltersExperimentActive && !(isBundleCLP && deviceType === DEVICE_TYPES.MOBILE),
          [`${styles.productCardsWrapper__new_clp_filters_desktop_group_b}`]: showDesktopFilterWithSidebar,
          [`${styles.wrapper__grouped_page}`]: !showMixedPage,
        })}
        ref={_ref}
      >
        {!showMixedPage ? (
          <div
            className={classnames(styles.titleWrapper__runtime, {
              [`${styles.titleWrapper__runtime__newClpFiltersExperiment}`]: isNewCLPFiltersExperimentActive,
            })}
          >
            <div
              data-content-type="title"
              className={classnames(styles.titleWrapper__runtime__title, {
                [`${styles.titleWrapper__runtime__title__experiment}`]: isNewCLPFiltersExperimentActive,
              })}
            >
              {getDisplayKey(subCollectionData, 'displayTitle', subCollectionData.title, isNewLocalisedFlow)}
            </div>
            {subTitleProps && (
              <RichText
                {...subTitleProps}
                className={classnames(styles.titleWrapper__runtime__subTitle, {
                  [`${styles.titleWrapper__runtime__subTitle__experiment}`]: isNewCLPFiltersExperimentActive,
                })}
              />
            )}
          </div>
        ) : null}
        {!isNewCLPFiltersExperimentActive ? <div className={styles.pseudoTitleSpacing} /> : null}

        <div
          className={classnames(styles.productCardsWrapper, {
            [`${styles.productCardsWrapper__new_clp_filters_experiment}`]: isNewCLPFiltersExperimentActive,
            [`${styles.productCardsWrapper__new_clp_filters_experiment_group_b}`]: showDesktopFilterWithSidebar,
          })}
          data-content-type="products"
        >
          {productCards?.map((productCard, idx) => {
            //TODO Handle runtime refresh of color order
            const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
            return (
              <ProductCard
                key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                productCard={productCard}
                className={styles.productCard}
                gender={collectionGender}
                optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                shouldShowMore
                productViewTracker={productViewTracker}
                productClickTrackConfig={{
                  trackInternal: true,
                  trackGA: true,
                  category: TRACKING_CATEGORY_NAVIGATION,
                  action: 'click_subcollection_product',
                  context: {
                    product_id: productCard.product.relatedIds.spid,
                    product_color: initialSelectedVariant?.displayConfig.value,
                    productPosition: idx,
                    ...trackingMetaData.context,
                  },
                  value: productCard.product.slug,
                }}
                onShowMoreClick={({ selectedVariant }) => {
                  navigateToPDP({
                    slug: productCard.product.slug,
                    tracker: tracker && `${tracker}__${subCollectionData.title}__${idx}`,
                    selectedOption: selectedVariant?.displayConfig.value,
                    additionalAttributes,
                    router,
                  })
                }}
                additionalAttributes={additionalAttributes}
                maxWidth={maxWidth}
                selectorKey={isFirstCollection && idx === 0 ? QUINCE_BOT_LOCATORS.COLLECTION.MOVE_TO_PDP : undefined}
                eagerLoadImage={isFirstCollection && idx < 4}
                tracker={tracker && `${tracker}__${subCollectionData.title}__${idx}`}
                imageRef={imageRef}
                showSecondImageOnProductTiles={showSecondImageOnProductTiles}
                showWhiteGloveDeliveryTag
              />
            )
          })}
        </div>
      </div>
    )
  },
)

SubCollectionSection.displayName = 'SubCollectionSection'

export default SubCollectionSection
